import type { LimitData } from '@repo/api-gw-sdk';

import type { Step } from '@/components/wizard/Step';
import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';

import { ConfigureProjectLimitsEditor } from './configureProjectLimitsEditor';

export interface ConfigureProjectLimitsEditorState {
  limits: Record<string, LimitData>;
  addedResourceOverrides: string[];
  removedResourceOverrides: string[];
}

const ConfigureProjectLimitsEditorFlow: Step<ConfigureProjectLimitsEditorState> =
  {
    name: 'Editor',
    Component: ConfigureProjectLimitsEditor,
  };

interface ConfigureProjectLimitsWizardProps {
  onSave: (limits: Record<string, LimitData>) => Promise<void>;
  onAbort: () => void;
}

export default function ConfigureProjectLimitsWizard(
  props: ConfigureProjectLimitsWizardProps
) {
  const { snackbar } = useWorkspace();
  const dal = useDAL();
  const { data: limitsData, mutate: mutateLimits } = dal.inventory.limits.get();

  const onFinish = async (state: ConfigureProjectLimitsEditorState) => {
    void Promise.all([
      ...state.addedResourceOverrides.map((resourceId) =>
        dal.inventory.limits.enableResourceOverrideLimits(resourceId)
      ),
      ...state.removedResourceOverrides.map((resourceId) =>
        dal.inventory.limits.disableResourceOverrideLimits(resourceId)
      ),
      dal.inventory.limits.set(state.limits),
    ]).then(() => {
      void mutateLimits();
      snackbar.showMessage('Project limits updated');
    });
    await props.onSave(state.limits);
    return true;
  };

  return (
    <Wizard<ConfigureProjectLimitsEditorState>
      onAbort={props.onAbort}
      hideBackArrow={true}
      hideBorder={false}
      onFinish={onFinish}
      flow={[ConfigureProjectLimitsEditorFlow]}
      initialState={{
        limits: limitsData ?? {},
        addedResourceOverrides: [],
        removedResourceOverrides: [],
      }}
      title={'Configure Project Limits'}
    />
  );
}
